import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { goalService, learningPlanService, roadmapService } from '../services/api';
import SideMenu from './SideMenu';

const KnowledgeAssessment = () => {
  const { goalId } = useParams();
  const navigate = useNavigate();
  
  const [goal, setGoal] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchGoalAndQuestions = async () => {
      try {
        const goalData = await goalService.getGoal(goalId);
        setGoal(goalData);
        
        const assessmentData = await goalService.generateKnowledgeAssessment(goalId);
        setQuestions(assessmentData.questions || []);
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load assessment');
        setLoading(false);
        console.error(err);
      }
    };

    fetchGoalAndQuestions();
  }, [goalId]);

  const handleAnswerChange = (questionIndex, answer) => {
    setAnswers({
      ...answers,
      [questionIndex]: answer
    });
  };

  const createLearningPlan = async () => {
    setIsSubmitting(true);
    try {
      // First create a roadmap for the goal
      const roadmap = await roadmapService.createRoadmap(goal.id);
      console.log('Created roadmap:', roadmap);
      
      // Then create the learning plan using the roadmap
      const assessmentResults = JSON.stringify(answers);
      const selectedDuration = '3 months';
      
      const learningPlan = await learningPlanService.createLearningPlan(
        goal.id,
        roadmap.id,
        assessmentResults,
        selectedDuration
      );
      
      navigate(`/learning-plan/${goal.id}`);
    } catch (error) {
      console.error('Error creating learning plan:', error);
      setError('Failed to create learning plan. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    createLearningPlan();
  };

  const handleSkip = () => {
    createLearningPlan();
  };

  if (loading) {
    return <div className="loading">Loading assessment...</div>;
  }

  return (
    <>
      <SideMenu />
      <div className="assessment-container">
        <h2>Knowledge Assessment</h2>
        <h3>Goal: {goal?.description}</h3>
        
        {error && <div className="error-message">{error}</div>}
        
        <p className="assessment-intro">
          Let's assess your current knowledge about this topic. This will help us create a personalized learning plan for you.
        </p>
        
        {questions.length === 0 ? (
          <div className="empty-state">
            <p>No assessment questions available. You can skip this step.</p>
            <button onClick={handleSkip} className="btn-primary">
              Skip Assessment
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {questions.map((question, index) => (
              <div key={index} className="question-card">
                <p className="question-text">{question}</p>
                <textarea
                  value={answers[index] || ''}
                  onChange={(e) => handleAnswerChange(index, e.target.value)}
                  placeholder="Your answer..."
                  rows={3}
                />
              </div>
            ))}
            
            <div className="form-actions">
              <button 
                type="button" 
                className="btn-secondary"
                onClick={handleSkip}
              >
                Skip Assessment
              </button>
              <button 
                type="submit" 
                className="btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating Learning Plan...' : 'Create Learning Plan'}
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  );
};

export default KnowledgeAssessment; 